import {AdvancedFilter} from "@/poster-gallery/classes/advanced-filters/advanced-filter";

/**
 * Class to handle view and url-building logic for when
 * a user updates their chosen content type (from the search bar / mobile modal)
 */
export class ContentTypeFilter extends AdvancedFilter {
    static advancedFiltersArrayKey = 'contentType';
    static pagePathUriIndex = 3;

    /**
     * @param {string} url
     * @return {string}
     */
    updateUrl(url) {
        if (PMW.posterGallery.isSearchPage(url)) {
            return this.updateUrlForSearchPage(url);
        }

        if (PMW.posterGallery.isEventTypePage(url)) {
            return this.updateUrlForEventTypePage(url);
        }
        return this.updateUrlForCategoryPage(url);
    }

    /**
     * @param {string} url
     * @return {string}
     */
    updateUrlForSearchPage(url) {
        let urlObj = new URL(url);

        if (this.isFilterActive()) {
            urlObj.searchParams.set('tt', this.filterOption);
        } else {
            urlObj.searchParams.delete('tt');
        }
        urlObj = this.updateUrlObjForConflictingMotionFilters(urlObj);
        return urlObj.toString();
    }

    /**
     * @param {string} url
     * @return {string}
     */
    updateUrlForEventTypePage(url) {
        let urlObj = new URL(url);

        if (this.isFilterActive()) {
            urlObj.searchParams.set(this.advancedFiltersArrayKey, this.filterOption);
        } else {
            urlObj.searchParams.delete(this.advancedFiltersArrayKey);
        }
        urlObj = this.updateUrlObjForConflictingMotionFilters(urlObj);
        return urlObj.toString();
    }

    /**
     * Updates a g/s/sizes page url with the current content type.
     * Content type is always the 4th segment (or the third index)
     * e.g. g/event-flyers/all/image -> [g, event-flyers, all, image]
     *
     * see makeMinimalGalleryUri in PosterDisplaySizeVO and PosterCategoryVO
     *
     * @param {string} url
     * @return {string}
     */
    updateUrlForCategoryPage(url) {
        const categoryUrlObj = new URL(url),
            pagePath = PMW.util.getPagePath(categoryUrlObj),
            pagePathUrisWithoutLeadingSlash = pagePath.split('/').slice(1),
            pagePathUrisLength = pagePathUrisWithoutLeadingSlash.length,
            contentTypePagePathIndex = ContentTypeFilter.pagePathUriIndex,
            doUrisHaveSegmentBeforeContentType = pagePathUrisLength > contentTypePagePathIndex - 1;

        // if the third segment is not set, then add 'all' for that segment, followed by the content type
        // e.g. g/event-flyers -> g/event-flyers/all/image
        if (!doUrisHaveSegmentBeforeContentType) {
            pagePathUrisWithoutLeadingSlash.splice(contentTypePagePathIndex - 1, 0, AdvancedFilter.allFilterOption, this.filterOption);
        } else {
            // if the third segment is set, then just insert/update the 4th segment
            // e.g. g/event-flyers/poster or g/event-flyers/poster/image -> g/event-flyers/poster/video
            pagePathUrisWithoutLeadingSlash.splice(contentTypePagePathIndex, 1, this.filterOption);
        }

        // if the last segments have their default value ('all'), then we remove them as they give redundant information. This also matches the canonical url
        // e.g. g/event-flyers/all/all -> g/event-flyers
        while (pagePathUrisWithoutLeadingSlash.length > 0 && pagePathUrisWithoutLeadingSlash[pagePathUrisWithoutLeadingSlash.length - 1] === AdvancedFilter.allFilterOption) {
            pagePathUrisWithoutLeadingSlash.pop();
        }
        const pagePathUriWithoutLeadingSlash = pagePathUrisWithoutLeadingSlash.join('/'),
            updatedUrl = PMW.util.site_url(pagePathUriWithoutLeadingSlash);

        let updatedUrlObj = new URL(updatedUrl);
        this.copySearchParamsToNewUrl(updatedUrlObj, categoryUrlObj);
        updatedUrlObj = this.updateUrlObjForConflictingMotionFilters(updatedUrlObj);
        return PMW.posterGallery.getPosterGalleryFiltersForUpdate().updateUrlForAdvancedFilters(updatedUrlObj).toString();
    }

    /**
     * Copies search params not related to filters from the oldUrl to the new url
     * e.g: gtm tags in the oldUrl get copied over
     *
     * @param {URL} newUrl
     * @param {URL} oldUrl
     */
    copySearchParamsToNewUrl(newUrl, oldUrl) {
        for (const [key, value] of oldUrl.searchParams) {
            newUrl.searchParams.set(key, value);
        }
    }

    updateAdvancedFilterInUrl(urlObj) {
        return new URL(this.updateUrl(urlObj.toString()));
    }

    updateView() {
        super.updateView();
        this.handleConflictingMotionFiltersInView();
    }

    /**
     * @return {boolean}
     */
    isImageOptionSelected() {
        return this.filterOption === 'image';
    }

    handleConflictingMotionFiltersInView() {
        const motionFilter = PMW.posterGallery.getPosterGalleryFiltersForUpdate().getMotionFilter();

        if (this.isImageOptionSelected()) {
            motionFilter.hideAllOptions();
            return;
        }
        motionFilter.showAllOptions();
    }

    /**
     * Handles the case where the selected subsize has a default content type (image/video) that the user
     * did not select themselves when landing on the current page
     */
    handleDefaultContentTypeForDisplaySize() {
        if (!this.isFilterActive()) {
            return;
        }
        const contentTypeInUrl = ContentTypeFilter.getValidFilterOption(this.getContentTypeFromUrl() ?? AdvancedFilter.allFilterOption);

        if (this.filterOption === contentTypeInUrl) {
            return;
        }
        this.setLastOptionSelectedByUser(contentTypeInUrl);
        this.setWasOptionSelectedByUser(false);
    }

    /**
     * @return {string|null}
     */
    getContentTypeFromUrl() {
        const url = window.location.href,
            urlObj = new URL(url);

        if (PMW.posterGallery.isSearchPage(url)) {
            return urlObj.searchParams.get('tt');
        }

        if (PMW.posterGallery.isEventTypePage(url)) {
            return urlObj.searchParams.get(this.advancedFiltersArrayKey);
        }
        const pagePath = PMW.util.getPagePath(urlObj),
            pagePathUrisWithoutLeadingSlash = pagePath.split('/').slice(1);
        return pagePathUrisWithoutLeadingSlash[ContentTypeFilter.pagePathUriIndex] ?? null;
    }

    /**
     * @param {URL} urlObj
     */
    updateUrlObjForConflictingMotionFilters(urlObj) {
        const posterGalleryFilters = PMW.posterGallery.getPosterGalleryFiltersForUpdate(),
            motionFilter = posterGalleryFilters.getMotionFilter();

        if (!motionFilter.wasOptionSelectedByUser) {
            motionFilter.revertToLastUserChosenOption();
        }

        if (this.isImageOptionSelected()) {
            posterGalleryFilters.removeMotionFilter();
            return posterGalleryFilters.getMotionFilter().updateAdvancedFilterInUrl(urlObj);
        }
        return urlObj;
    }
}